import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { formatIntegerBr, KPI_LOADING_DISPLAY } from '../../lib/managerial-kpis'
import type { OrderStatusBreakdown } from '../../lib/order-status'
import styles from './OrderStatusChart.module.css'

interface OrderStatusChartProps {
  breakdown: OrderStatusBreakdown
  isLoading: boolean
  isBusy?: boolean
  hasData?: boolean
}

export function OrderStatusChart({
  breakdown,
  isLoading,
  isBusy = false,
  hasData = true,
}: OrderStatusChartProps) {
  const pieSlices = breakdown.slices.filter((slice) => slice.count > 0)
  const showEmptyRecorte = hasData && breakdown.total === 0
  const donutContent = isLoading ? (
    <p className={styles.empty}>Carregando pedidos...</p>
  ) : showEmptyRecorte ? (
    <p className={styles.empty}>Nenhum pedido no recorte.</p>
  ) : breakdown.total === 0 ? (
    <p className={styles.empty}>{KPI_LOADING_DISPLAY}</p>
  ) : (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={pieSlices}
            dataKey="count"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius="68%"
            outerRadius="92%"
            paddingAngle={pieSlices.length > 1 ? 2 : 0}
            stroke="#fff"
            strokeWidth={3}
            startAngle={90}
            endAngle={-270}
          >
            {pieSlices.map((slice) => (
              <Cell key={slice.id} fill={slice.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => {
              const numericValue = typeof value === 'number' ? value : Number(value ?? 0)
              return [formatIntegerBr(numericValue), String(name ?? '')]
            }}
            contentStyle={{
              borderRadius: 10,
              border: '1px solid var(--color-border-subtle)',
              fontSize: 12,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className={styles.centerLabel}>
        <strong className={styles.centerValue}>{breakdown.formattedTotal}</strong>
        <span className={styles.centerCaption}>Pedidos</span>
      </div>
    </>
  )

  return (
    <section
      className={styles.card}
      aria-labelledby="order-status-title"
      aria-busy={isLoading || isBusy}
    >
      <div className={styles.header}>
        <h2 id="order-status-title" className={styles.title}>
          Status dos Pedidos
        </h2>
      </div>

      <div className={styles.body}>
        <div className={styles.donutWrap}>{donutContent}</div>

        <ul className={styles.legend} aria-label="Distribuição dos pedidos">
          {breakdown.slices.map((slice) => (
            <li key={slice.id} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: slice.color }} />
              <span className={styles.legendName}>{slice.label}</span>
              <span className={styles.legendValue}>
                {slice.formattedCount} ({slice.formattedPercent})
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
